import { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, XCircle, Users, Briefcase, FileCheck, Star, BookOpen, Eye, Check, X as XIcon, Mail, Banknote, Globe, UserCheck, Scale, Activity, AlertTriangle, RefreshCw } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [allExperts, setAllExperts] = useState([]);
  const [pendingArticles, setPendingArticles] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [externalExperts, setExternalExperts] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [claims, setClaims] = useState([]);
  const [consultationPricing, setConsultationPricing] = useState(null);
  const [affiliationPricing, setAffiliationPricing] = useState(null);
  const [legalSources, setLegalSources] = useState([]);
  const [apiUsage, setApiUsage] = useState({ items: [], aggregates_by_source: {} });
  const [recoLogs, setRecoLogs] = useState([]);
  const [diagBusy, setDiagBusy] = useState(false);

  const refresh = useCallback(() => {
    apiClient.get("/admin/stats").then(r => setStats(r.data));
    apiClient.get("/admin/experts/pending").then(r => setPending(r.data));
    apiClient.get("/admin/experts").then(r => setAllExperts(r.data));
    apiClient.get("/articles/admin/all", { params: { status: "pending" } }).then(r => setPendingArticles(r.data));
    apiClient.get("/articles/admin/all").then(r => setAllArticles(r.data));
    apiClient.get("/admin/external-experts").then(r => setExternalExperts(r.data?.items || []));
    apiClient.get("/admin/affiliation-invitations").then(r => setInvitations(r.data?.items || []));
    apiClient.get("/admin/affiliation-claims").then(r => setClaims(r.data?.items || []));
    apiClient.get("/admin/consultation-pricing").then(r => setConsultationPricing(r.data?.pricing || null));
    apiClient.get("/admin/affiliation-pricing").then(r => setAffiliationPricing(r.data?.pricing || null));
    apiClient.get("/legal-sources/status").then(r => setLegalSources(r.data?.sources || []));
    apiClient.get("/admin/api-usage?limit=50").then(r => setApiUsage(r.data || { items: [], aggregates_by_source: {} }));
    apiClient.get("/admin/recommendation-logs?limit=20").then(r => setRecoLogs(r.data?.items || []));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const updateConsultationPricing = async (key, value) => {
    try {
      const r = await apiClient.patch("/admin/consultation-pricing", { [`${key}_eur`]: Number(value) });
      setConsultationPricing(r.data?.pricing);
      toast.success("Tarif mis à jour");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const updateAffiliationPricing = async (key, value) => {
    try {
      const r = await apiClient.patch("/admin/affiliation-pricing", { [key]: Number(value) });
      setAffiliationPricing(r.data?.pricing);
      toast.success("Tarif d'affiliation mis à jour");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const updateExternalStatus = async (id, status) => {
    try {
      await apiClient.post(`/admin/external-experts/${id}/status`, { status });
      setExternalExperts((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Erreur");
    }
  };

  const verify = async (id, val) => {
    try {
      await apiClient.post(`/admin/experts/${id}/verify`, { verified: val });
      toast.success(val ? "Spécialiste référencé" : "Référencement retiré");
      refresh();
    } catch (e) { toast.error("L'opération a échoué"); }
  };

  const moderateArticle = async (id, status, reason) => {
    try {
      await apiClient.post(`/articles/${id}/moderate`, {
        status,
        rejection_reason: status === "rejected" ? (reason || "Motif non précisé") : null,
      });
      toast.success(status === "published" ? "Article publié" : "Article refusé");
      refresh();
    } catch (e) { toast.error("L'opération a échoué"); }
  };

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <Navbar variant="light" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">Administration</div>
            <h1 className="font-serif text-4xl text-[#0B3A2A] tracking-tight">Tableau de bord CONFILEX</h1>
          </div>
          <a
            href="/admin/cockpit"
            data-testid="link-cockpit"
            className="text-sm px-4 py-2 rounded-full bg-[#0B3A2A] text-[#C9A961] border border-[#C9A961]/30 hover:bg-[#0B3A2A] transition"
          >
            Cockpit IA · Pilotage
          </a>
          <a
            href="/admin/experts/manage"
            data-testid="link-experts-manage"
            className="text-sm px-4 py-2 rounded-full bg-[#C9A961] text-[#0B3A2A] hover:bg-[#E3C77A] transition ml-2 font-medium"
          >
            Fiches Spécialistes
          </a>
          <a
            href="/admin/supervision"
            data-testid="link-supervision"
            className="text-sm px-4 py-2 rounded-full bg-white text-[#0B3A2A] border border-[#0B3A2A]/20 hover:bg-slate-50 transition ml-2"
          >
            Supervision & Audit
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {stats && [
            { l: "Utilisateurs", v: stats.users, I: Users },
            { l: "Spécialistes référencés", v: stats.experts_verified, I: ShieldCheck },
            { l: "Dossiers actifs", v: stats.missions_active, I: Briefcase },
            { l: "Avis", v: stats.reviews, I: Star },
          ].map(s => (
            <Card key={s.l} className="p-5 rounded-2xl border-slate-100">
              <s.I className="w-5 h-5 text-[#C9A961] mb-3" />
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{s.l}</div>
              <div className="font-serif text-3xl text-[#0B3A2A]">{s.v}</div>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="rounded-full p-1 bg-slate-100 flex-wrap h-auto">
            <TabsTrigger value="pending" className="rounded-full" data-testid="tab-pending">À examiner ({pending.length})</TabsTrigger>
            <TabsTrigger value="all" className="rounded-full" data-testid="tab-all">Réseau référencé ({allExperts.length})</TabsTrigger>
            <TabsTrigger value="articles" className="rounded-full" data-testid="tab-articles">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Articles ({pendingArticles.length})
            </TabsTrigger>
            <TabsTrigger value="external" className="rounded-full" data-testid="tab-external">
              <Globe className="w-3.5 h-3.5 mr-1.5" /> Experts externes ({externalExperts.length})
            </TabsTrigger>
            <TabsTrigger value="invitations" className="rounded-full" data-testid="tab-invitations">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Invitations ({invitations.length})
            </TabsTrigger>
            <TabsTrigger value="claims" className="rounded-full" data-testid="tab-claims">
              <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Revendications ({claims.length})
            </TabsTrigger>
            <TabsTrigger value="pricing" className="rounded-full" data-testid="tab-pricing">
              <Banknote className="w-3.5 h-3.5 mr-1.5" /> Tarifs
            </TabsTrigger>
            <TabsTrigger value="legal" className="rounded-full" data-testid="tab-legal-intelligence">
              <Scale className="w-3.5 h-3.5 mr-1.5" /> Sources juridiques ({legalSources.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pending">
            {pending.length === 0 ? <p className="text-slate-500">Aucune candidature en cours d'examen.</p> : (
              <div className="space-y-3">
                {pending.map(e => (
                  <Card key={e.id} className="p-5 rounded-2xl border-slate-100" data-testid={`pending-${e.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex gap-4 flex-1 min-w-0">
                        {e.photo && <img src={e.photo} alt="" className="w-16 h-16 rounded-full object-cover" />}
                        <div className="min-w-0">
                          <h3 className="font-serif text-lg text-[#0B3A2A]">{e.first_name} {e.last_name}</h3>
                          <p className="text-sm text-slate-600">{e.profession} · {e.city}</p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{e.description}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {(e.specialties || []).map(s => <Badge key={s} className="bg-slate-100 text-slate-700 text-xs">{s}</Badge>)}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => verify(e.id, true)} data-testid={`verify-${e.id}`}
                          className="rounded-full bg-[#7A9B7E] hover:bg-[#93B19A]"><FileCheck className="w-4 h-4 mr-1" />Référencer</Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="all">
            <div className="space-y-3">
              {allExperts.map(e => (
                <Card key={e.id} className="p-5 rounded-2xl border-slate-100">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      {e.photo && <img src={e.photo} alt="" className="w-12 h-12 rounded-full object-cover" />}
                      <div>
                        <div className="font-medium text-[#0B3A2A]">{e.first_name} {e.last_name}</div>
                        <div className="text-xs text-slate-500">{e.profession} · {e.city}</div>
                      </div>
                      {e.verified && <Badge className="bg-[#7A9B7E]/15 text-[#5C7D62] rounded-full"><ShieldCheck className="w-3 h-3 mr-1" />Référencé</Badge>}
                    </div>
                    {e.verified ? (
                      <Button size="sm" variant="outline" onClick={() => verify(e.id, false)} className="rounded-full">
                        <XCircle className="w-4 h-4 mr-1" />Retirer du réseau
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => verify(e.id, true)} className="rounded-full bg-[#7A9B7E] hover:bg-[#93B19A]">
                        <FileCheck className="w-4 h-4 mr-1" />Référencer
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="articles">
            {pendingArticles.length === 0 ? (
              <div className="bg-white rounded-2xl border-slate-100 border p-6 text-slate-500 mb-6">
                Aucun article en attente de relecture.
              </div>
            ) : (
              <div className="space-y-4 mb-8">
                <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961]">Articles en attente</div>
                {pendingArticles.map(a => (
                  <ArticleModerationCard key={a.id} article={a} onModerate={moderateArticle} />
                ))}
              </div>
            )}
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Tous les articles ({allArticles.length})</div>
            <div className="space-y-2">
              {allArticles.map(a => (
                <Card key={a.id} className="p-4 rounded-2xl border-slate-100">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-medium text-[#0B3A2A] truncate">{a.title || "Sans titre"}</h4>
                        <Badge className={`rounded-full text-xs ${
                          a.status === "published" ? "bg-emerald-100 text-emerald-800" :
                          a.status === "pending" ? "bg-amber-100 text-amber-800" :
                          a.status === "rejected" ? "bg-rose-100 text-rose-800" :
                          "bg-slate-100 text-slate-700"
                        }`}>{a.status}</Badge>
                      </div>
                      <div className="text-xs text-slate-500">
                        {a.author_name} · {new Date(a.updated_at).toLocaleDateString("fr-FR")}
                        {a.view_count ? ` · ${a.view_count} vues` : ""}
                      </div>
                    </div>
                    {a.status === "published" && (
                      <Button asChild size="sm" variant="outline" className="rounded-full">
                        <a href={`/insights/${a.slug}`} target="_blank" rel="noreferrer">
                          <Eye className="w-4 h-4 mr-1.5" />Consulter
                        </a>
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* EXTERNAL EXPERTS */}
          <TabsContent value="external">
            {externalExperts.length === 0 ? (
              <p className="text-slate-500">Aucun spécialiste externe identifié pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {externalExperts.map((e) => (
                  <Card key={e.id} className="p-4 rounded-2xl border-slate-100" data-testid={`ext-expert-${e.id}`}>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-serif text-[#0B3A2A]">{e.display_name}</h4>
                          <Badge className={`text-[10px] uppercase tracking-wider rounded-full ${
                            e.status === "affiliated_active" ? "bg-emerald-100 text-emerald-800" :
                            e.status === "claimed_pending" ? "bg-amber-100 text-amber-800" :
                            e.status === "invitation_sent" ? "bg-amber-50 text-amber-900 border border-amber-200" :
                            e.status === "suspended" ? "bg-rose-100 text-rose-800" :
                            "bg-slate-100 text-slate-700"
                          }`}>{e.status?.replace(/_/g, " ")}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {e.profession || "—"} · {e.city || "—"} · invitations : <span className="text-[#C9A961] font-semibold">{e.invitation_count || 0}</span>
                        </p>
                      </div>
                      <select
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700"
                        value={e.status}
                        onChange={(ev) => updateExternalStatus(e.id, ev.target.value)}
                        data-testid={`ext-status-${e.id}`}
                      >
                        <option value="public_identified">Profil public identifié</option>
                        <option value="invitation_sent">Invitation envoyée</option>
                        <option value="claimed_pending">Revendication en cours</option>
                        <option value="verified_not_affiliated">Vérifié non affilié</option>
                        <option value="affiliated_active">Affilié actif</option>
                        <option value="suspended">Suspendu</option>
                        <option value="removed_by_request">Retiré sur demande</option>
                      </select>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* INVITATIONS */}
          <TabsContent value="invitations">
            {invitations.length === 0 ? (
              <p className="text-slate-500">Aucune invitation envoyée.</p>
            ) : (
              <div className="space-y-2">
                {invitations.map((inv) => (
                  <Card key={inv.id} className="p-4 rounded-2xl border-slate-100">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#0B3A2A] font-medium">{inv.external_id}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Demandée par <span className="text-[#C9A961]">{inv.user_email}</span> · {new Date(inv.created_at).toLocaleString("fr-FR")}
                        </p>
                        {inv.message && (
                          <p className="text-xs text-slate-600 italic mt-1 line-clamp-2">« {inv.message} »</p>
                        )}
                      </div>
                      <Badge className="bg-amber-50 text-amber-900 border border-amber-200 text-[10px] uppercase tracking-wider rounded-full">
                        {inv.status || "envoyée"}
                      </Badge>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CLAIMS */}
          <TabsContent value="claims">
            {claims.length === 0 ? (
              <p className="text-slate-500">Aucune revendication en cours.</p>
            ) : (
              <div className="space-y-2">
                {claims.map((c) => (
                  <Card key={c.id} className="p-4 rounded-2xl border-amber-200 bg-amber-50/30">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#0B3A2A] font-medium">
                          Revendication · {c.external_id}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          ID pro : <span className="font-mono">{c.professional_id}</span> · {c.contact_email}
                          {c.contact_phone ? ` · ${c.contact_phone}` : ""}
                        </p>
                        {c.biography && (
                          <p className="text-xs text-slate-600 mt-2 italic line-clamp-3">« {c.biography} »</p>
                        )}
                        {c.declared_specialties?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {c.declared_specialties.map((s) => (
                              <Badge key={s} className="bg-slate-100 text-slate-700 text-xs">{s}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Badge className="bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wider rounded-full">
                          {c.status?.replace(/_/g, " ") || "en cours"}
                        </Badge>
                        <Button size="sm" variant="outline" className="rounded-full text-xs"
                          onClick={() => updateExternalStatus(c.external_id, "verified_not_affiliated")}>
                          <Check className="w-3 h-3 mr-1" /> Valider l'identité
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PRICING */}
          <TabsContent value="pricing">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-6 rounded-2xl border-slate-100">
                <h3 className="font-serif text-xl text-[#0B3A2A] mb-1">Tarifs consultation</h3>
                <p className="text-xs text-slate-500 mb-5">Tarifs facturés à l'utilisateur final pour les consultations CONFILEX.</p>
                {consultationPricing && (
                  <div className="space-y-4">
                    {Object.entries(consultationPricing).map(([key, val]) => (
                      <PricingRow
                        key={key}
                        label={val.label}
                        description={val.description}
                        value={val.amount_eur}
                        suffix="€ TTC"
                        onSave={(v) => updateConsultationPricing(key, v)}
                        testid={`price-cons-${key}`}
                      />
                    ))}
                  </div>
                )}
              </Card>

              <Card className="p-6 rounded-2xl border-slate-100">
                <h3 className="font-serif text-xl text-[#0B3A2A] mb-1">Modèle économique affiliation</h3>
                <p className="text-xs text-slate-500 mb-5">Conditions présentées aux professionnels lors de leur revendication.</p>
                {affiliationPricing && (
                  <div className="space-y-4">
                    <PricingRow
                      label={affiliationPricing.monthly_subscription.label}
                      description={affiliationPricing.monthly_subscription.description}
                      value={affiliationPricing.monthly_subscription.amount_eur}
                      suffix="€ HT / mois"
                      onSave={(v) => updateAffiliationPricing("monthly_subscription_eur", v)}
                      testid="price-aff-mensuel"
                    />
                    <PricingRow
                      label={affiliationPricing.lead_fee.label}
                      description={affiliationPricing.lead_fee.description}
                      value={affiliationPricing.lead_fee.amount_eur}
                      suffix="€ HT / dossier"
                      onSave={(v) => updateAffiliationPricing("lead_fee_eur", v)}
                      testid="price-aff-lead"
                    />
                    <PricingRow
                      label={affiliationPricing.tech_commission_pct.label}
                      description={affiliationPricing.tech_commission_pct.description}
                      value={affiliationPricing.tech_commission_pct.amount_pct}
                      suffix="%"
                      onSave={(v) => updateAffiliationPricing("tech_commission_pct", v)}
                      testid="price-aff-commission"
                    />
                    <PricingRow
                      label={affiliationPricing.premium_visibility.label}
                      description={affiliationPricing.premium_visibility.description}
                      value={affiliationPricing.premium_visibility.amount_eur}
                      suffix="€ HT / mois"
                      onSave={(v) => updateAffiliationPricing("premium_visibility_eur", v)}
                      testid="price-aff-premium"
                    />
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="legal">
            <LegalIntelligencePanel
              sources={legalSources}
              apiUsage={apiUsage}
              recoLogs={recoLogs}
              busy={diagBusy}
              onClearCache={async (source) => {
                try {
                  await apiClient.post("/admin/legal-sources/sync", source ? { source } : {});
                  toast.success(source ? `Cache ${source} vidé` : "Cache vidé");
                  refresh();
                } catch {
                  toast.error("Erreur lors du vidage du cache");
                }
              }}
              onRunDiagnostics={async () => {
                setDiagBusy(true);
                try {
                  const r = await apiClient.get("/admin/legal-sources/diagnostics");
                  setLegalSources(r.data?.diagnostics || []);
                  toast.success("Diagnostic terminé");
                } catch {
                  toast.error("Échec du diagnostic");
                } finally {
                  setDiagBusy(false);
                }
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function ArticleModerationCard({ article, onModerate }) {
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <Card className="p-5 rounded-2xl border-amber-200 bg-amber-50/30" data-testid={`pending-article-${article.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg text-[#0B3A2A] mb-1.5">{article.title}</h3>
          <p className="text-sm text-slate-600 line-clamp-2 mb-2">{article.excerpt || article.body?.slice(0, 220)}</p>
          <div className="text-xs text-slate-500">
            {article.author_name} · {(article.profession_tags || []).join(", ")} · {article.reading_time || 1} min
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild size="sm" variant="outline" className="rounded-full">
            <a href={`/expert/articles/${article.id}`} target="_blank" rel="noreferrer">
              <Eye className="w-4 h-4 mr-1.5" />Lire
            </a>
          </Button>
          <Button size="sm" className="rounded-full bg-[#7A9B7E] hover:bg-[#93B19A]"
            onClick={() => onModerate(article.id, "published")}
            data-testid={`approve-article-${article.id}`}>
            <Check className="w-4 h-4 mr-1.5" />Publier
          </Button>
          <Button size="sm" variant="outline" className="rounded-full"
            onClick={() => setShowReject(s => !s)}
            data-testid={`reject-article-toggle-${article.id}`}>
            <XIcon className="w-4 h-4 mr-1.5" />Refuser
          </Button>
        </div>
      </div>
      {showReject && (
        <div className="mt-4 pt-4 border-t border-amber-200">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motif du retour éditorial (transmis à l'auteur)…"
            data-testid={`reject-reason-${article.id}`}
            className="w-full p-3 rounded-lg border border-slate-200 text-sm resize-none"
            rows={2}
          />
          <div className="flex justify-end mt-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setShowReject(false); setReason(""); }}>
              Annuler
            </Button>
            <Button size="sm" className="rounded-full bg-rose-600 hover:bg-rose-700 text-white ml-2"
              onClick={() => { onModerate(article.id, "rejected", reason); setShowReject(false); setReason(""); }}
              data-testid={`reject-confirm-${article.id}`}>
              Confirmer le refus
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}


function PricingRow({ label, description, value, suffix, onSave, testid }) {
  const [local, setLocal] = useState(value);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setLocal(value); setDirty(false); }, [value]);
  const onChange = (v) => { setLocal(v); setDirty(Number(v) !== Number(value)); };
  return (
    <div className="border-b border-slate-100 pb-4 last:border-0 last:pb-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-[#0B3A2A] font-medium">{label}</p>
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            type="number"
            step="0.5"
            min="0"
            value={local}
            onChange={(e) => onChange(e.target.value)}
            className="w-24 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right tabular-nums focus:outline-none focus:border-[#C9A961]"
          />
          <span className="text-xs text-slate-500 whitespace-nowrap">{suffix}</span>
          {dirty && (
            <button
              onClick={() => onSave(local)}
              className="text-xs px-3 py-1.5 rounded-lg bg-[#0B3A2A] text-white hover:brightness-110"
            >
              Enregistrer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LegalIntelligencePanel({ sources, apiUsage, recoLogs, busy, onClearCache, onRunDiagnostics }) {
  const aggregates = apiUsage?.aggregates_by_source || {};
  return (
    <div className="space-y-6" data-testid="legal-intelligence-panel">
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-serif italic text-xl text-[#0B3A2A]">Sources juridiques connectées</h3>
            <p className="text-xs text-slate-500 mt-1">
              Tous les appels passent par le backend — aucune clé exposée au frontend.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="legal-diag-btn"
              onClick={onRunDiagnostics}
              disabled={busy}
              className="text-xs px-3 py-2 rounded-lg bg-[#0B3A2A] text-white hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Activity className="w-3.5 h-3.5" /> Diagnostiquer
            </button>
            <button
              data-testid="legal-cache-clear-btn"
              onClick={() => onClearCache(null)}
              className="text-xs px-3 py-2 rounded-lg border border-slate-200 text-[#0B3A2A] hover:bg-slate-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Vider le cache
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {sources.map((s) => {
            const agg = aggregates[s.name] || { calls: 0, errors: 0, cached: 0, mocked: 0 };
            const badgeColor =
              s.mode === "live" ? "bg-emerald-100 text-emerald-700"
                : s.mode === "disabled" ? "bg-slate-100 text-slate-500"
                : "bg-amber-100 text-amber-700";
            return (
              <div key={s.name} data-testid={`legal-source-${s.name}`} className="border border-slate-100 rounded-xl p-4 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-[#0B3A2A]">{s.label}</p>
                    <p className="text-[11px] text-slate-500">{s.name}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${badgeColor}`}>
                    {s.mode}
                  </span>
                </div>
                {s.missing_env && s.missing_env.length > 0 && (
                  <p className="text-xs text-slate-500 mb-2 flex items-start gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                    Variables manquantes : <span className="font-mono">{s.missing_env.join(", ")}</span>
                  </p>
                )}
                <div className="flex flex-wrap gap-3 text-[11px] text-slate-600 mt-2">
                  <span>Appels : <strong>{agg.calls}</strong></span>
                  <span>Erreurs : <strong className={agg.errors > 0 ? "text-red-600" : ""}>{agg.errors}</strong></span>
                  <span>Cache : <strong>{agg.cached}</strong></span>
                  <span>Mock : <strong>{agg.mocked}</strong></span>
                  <span>Fiabilité : <strong>{Math.round((s.reliability_score || 0) * 100)}%</strong></span>
                </div>
                {s.mode !== "disabled" && (
                  <button
                    data-testid={`legal-source-clear-${s.name}`}
                    onClick={() => onClearCache(s.name)}
                    className="mt-2 text-[11px] text-[#C9A961] hover:underline"
                  >
                    Vider le cache de cette source
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500 italic mt-4 leading-relaxed">
          CONFILEX ne scrape pas Doctrine et ne contourne aucun paywall. Le connecteur Doctrine n'est activable que via accès API officiel.
        </p>
      </Card>

      <Card className="p-6">
        <h3 className="font-serif italic text-lg text-[#0B3A2A] mb-3">Derniers appels API ({apiUsage.items?.length || 0})</h3>
        {(!apiUsage.items || apiUsage.items.length === 0) ? (
          <p className="text-sm text-slate-500">Aucun appel enregistré pour l'instant.</p>
        ) : (
          <div className="max-h-72 overflow-auto text-xs">
            <table className="w-full">
              <thead className="text-slate-500 text-left">
                <tr><th className="py-1.5">Heure</th><th>Source</th><th>Opération</th><th>Durée</th><th>Statut</th><th>Mode</th></tr>
              </thead>
              <tbody>
                {apiUsage.items.slice(0, 30).map((it) => (
                  <tr key={it.id} className="border-t border-slate-100">
                    <td className="py-1.5 text-slate-500">{(it.created_at || "").slice(11, 19)}</td>
                    <td className="font-mono">{it.source}</td>
                    <td>{it.operation}</td>
                    <td className="tabular-nums">{it.duration_ms} ms</td>
                    <td>{it.ok ? <span className="text-emerald-600">OK</span> : <span className="text-red-600">ERR</span>}</td>
                    <td>{it.cached ? "cache" : (it.mocked ? "mock" : "live")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="font-serif italic text-lg text-[#0B3A2A] mb-3">Recommandations récentes ({recoLogs?.length || 0})</h3>
        {(!recoLogs || recoLogs.length === 0) ? (
          <p className="text-sm text-slate-500">Aucune recommandation enregistrée.</p>
        ) : (
          <ul className="space-y-2 text-xs">
            {recoLogs.slice(0, 10).map((l) => (
              <li key={l.id} className="border border-slate-100 rounded-lg p-3">
                <div className="flex justify-between mb-1">
                  <span className="text-slate-500">{(l.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="text-[#0B3A2A]">{l.returned_count} expert(s) suggéré(s) sur {l.candidate_count} candidat(s)</span>
                </div>
                <p className="text-slate-700 italic line-clamp-2">{l.description_excerpt}</p>
                <p className="text-[10px] text-slate-500 mt-1">
                  Types : {(l.required_types || []).join(", ") || "—"} · Matières : {(l.detected_topics || []).slice(0, 3).join(", ") || "—"}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

